export default {
  article: {
    metaTitle: 'Basetao Spreadsheet Guide 2026: How to Organize Your Shopping',
    metaDescription:
      'Learn how to build and use a Basetao spreadsheet to organize product links, sellers, QC photos and shipping details in one clean place.',
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Basetao Spreadsheet Guide',
    title: 'The Complete Basetao Spreadsheet Guide (2026)',
    subtitle:
      'A practical guide to building and using a Basetao spreadsheet to organize product links, sellers, QC photos and shipping details.',
    authorLabel: 'By',
    author: 'Basetao Spreadsheet Team',
    updatedLabel: 'Last updated',
    updated: 'August 2026',
    readingTime: '8 min read',
    tocLabel: 'On this page',
    intro: {
      heading: 'Introduction',
      p1: 'Shopping through a Chinese purchasing agent like Basetao opens the door to a huge range of products — from sneakers and clothing to accessories and everyday essentials. But once your cart starts to fill up, keeping track of every item, seller, price and QC photo can quickly become overwhelming.',
      p2: 'That is where a Basetao spreadsheet comes in. It is a simple, organized way to record everything you are buying, so you always know what you have ordered, where it is and how much it cost. This guide walks you through everything you need to build and use one effectively.',
    },
    whatIsBasetao: {
      heading: 'What is Basetao?',
      p1: 'Basetao is a shopping agent that helps international buyers purchase items from Chinese online platforms such as Taobao, 1688 and Weidian. It acts as a middle layer: you paste a product link, the agent buys the item for you, stores it in their warehouse and then ships it to your country. Along the way you can request QC (quality check) photos to confirm the item looks right before it is shipped.',
    },
    whatIsSpreadsheet: {
      heading: 'What is a Basetao Spreadsheet?',
      p1: 'A Basetao spreadsheet is a shared document — usually built in Google Sheets or Excel — that lists every product you are interested in or have ordered through the agent. It typically records the product link, the seller, the price, the size or color, the QC status and the shipping progress.',
      p2: 'Think of it as your personal control center for a Basetao haul. Instead of juggling dozens of browser tabs and chat messages, you keep everything in one place that you can update and review at a glance.',
    },
    why: {
      heading: 'Why use a Basetao Spreadsheet?',
      p1: 'A well-maintained spreadsheet saves time and prevents costly mistakes. Here are the main benefits:',
      items: [
        'Keep track of many items at once — especially useful for large hauls.',
        'Compare prices and sellers before you commit to a purchase.',
        'Record QC results so you can decide quickly whether to keep or return an item.',
        'Monitor warehouse and shipping status without searching through chat logs.',
        'Calculate a realistic total cost including agent fees and shipping.',
      ],
    },
    whatToInclude: {
      heading: 'What to include in your spreadsheet',
      p1: 'The exact columns you need depend on how you shop, but these fields are useful for almost everyone:',
      items: [
        'Product link — the original URL so you can reopen the listing anytime.',
        'Seller name — to compare quality between different stores.',
        'Price — in the local currency and your own currency.',
        'Size, color and quantity — to avoid ordering the wrong variant.',
        'QC status and photo link — to track which items passed inspection.',
        'Warehouse status — arrived, stored or ready to ship.',
        'Shipping method and tracking number — for the final delivery.',
      ],
    },
    structure: {
      heading: 'How to structure your spreadsheet',
      p1: 'A clear column layout makes your spreadsheet easy to scan and filter. A practical starting structure looks like this:',
      columns: [
        { col: 'Product name', desc: 'A short, clear name so you can find items fast.' },
        { col: 'Product link', desc: 'The full URL to reopen the listing when needed.' },
        { col: 'Seller', desc: 'The store name, useful for comparing quality.' },
        { col: 'Price', desc: 'Local price and converted price in your currency.' },
        { col: 'Size / color', desc: 'The exact variant you plan to buy.' },
        { col: 'QC', desc: 'A quick yes/no or a link to the QC photos.' },
        { col: 'Status', desc: 'Ordered, in warehouse, or shipped.' },
      ],
    },
    tips: {
      heading: 'Tips for staying organized',
      items: [
        'Use one row per item so you can filter and sort easily.',
        'Color-code rows by status to see progress at a glance.',
        'Keep the original product link — listings can change or disappear.',
        'Update the sheet right after each action instead of relying on memory.',
        'Back up the spreadsheet or share it with yourself in the cloud.',
      ],
    },
    mistakes: {
      heading: 'Common mistakes to avoid',
      items: [
        'Skipping QC and only discovering problems after the item ships.',
        'Forgetting to note the exact size or color, leading to wrong orders.',
        'Not tracking shipping costs, which makes the final total a surprise.',
        'Using unclear product names that are impossible to search later.',
        'Leaving the spreadsheet outdated, so it no longer reflects reality.',
      ],
    },
    faq: {
      heading: 'Frequently asked questions',
      items: [
        {
          q: 'Is a Basetao spreadsheet the same as the official Basetao site?',
          a: 'No. It is an independent organizational tool. It is not affiliated with or part of the official Basetao website unless explicitly stated.',
        },
        {
          q: 'Do I need a spreadsheet for a small order?',
          a: 'Not strictly, but even a few items become easier to manage when the links, prices and QC status are all in one place.',
        },
        {
          q: 'Which tool should I use to build the spreadsheet?',
          a: 'Google Sheets and Excel are both popular. Google Sheets is easy to access from any device and simple to share.',
        },
        {
          q: 'What should I do if a product listing disappears?',
          a: 'Keep the seller name and a clear description in your sheet, and reach out to the agent with the original link if you still have it.',
        },
        {
          q: 'Does this website sell products directly?',
          a: 'No. This site is an informational resource. Product categories link out to the main website where you can browse and buy.',
        },
      ],
    },
    cta: {
      title: 'Ready to organize your next Basetao haul?',
      desc: 'Explore the curated product categories or head over to the main website to browse the full catalog.',
    },
  },
};